/** @jsx jsx */
import type { ReactElement, ReactNode } from 'react';
import { Button } from '@balance-web/button';
import { jsx } from '@balance-web/core';
import { Heading } from '@balance-web/heading';
import { Inline } from '@balance-web/inline';
import { Stack } from '@balance-web/stack';
import { forwardRefWithAs, makeId, useId } from '@balance-web/utils';
import { Text } from '@balance-web/text';

import type { ModalSize } from './size';
import { useModalWidth } from './size';
import { DialogBase } from './DialogBase';

type Action = {
  action: () => void;
  label: string;
};

type InfoDialogProps = {
  actions: {
    cancel?: Action;
    confirm: Action & { loading?: boolean };
  };
  /** Use any of our spot or hero illustrations here. */
  illustration: ReactElement;
  children: ReactNode;
  title: string;
  id?: string;
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
};

export const InfoDialog = forwardRefWithAs<'div', InfoDialogProps>(
  (
    {
      actions,
      children,
      illustration,
      title,
      id,
      isOpen,
      onClose,
      size = 'small',
      ...props
    },
    ref
  ) => {
    const { cancel, confirm } = actions;
    const instanceId = useId(id);
    const headingId = makeId(instanceId, 'heading');

    const modalWidth = useModalWidth(size);

    return (
      <DialogBase
        ref={ref}
        aria-labelledby={headingId}
        autoFocus={false}
        id={instanceId}
        isOpen={isOpen}
        width={modalWidth}
        onClose={onClose}
        {...props}
      >
        <Stack
          align="center"
          css={{
            textAlign: 'center',
          }}
          gap="xxlarge"
          padding="xlarge"
        >
          {illustration}
          <Stack gap="medium">
            <Heading as="h2" id={headingId} level="3">
              {title}
            </Heading>
            <Text color="textPrimary">{children}</Text>
          </Stack>

          <Inline gap="large">
            {cancel ? (
              <Button
                key={cancel.label}
                colorScheme="tertiary"
                disabled={confirm.loading}
                id={makeId(instanceId, 'cancel')}
                label={cancel.label}
                size="small"
                variant="filled"
                onClick={cancel.action}
              />
            ) : null}
            <Button
              key={confirm.label}
              id={makeId(instanceId, 'confirm')}
              label={confirm.label}
              loading={confirm.loading}
              size="small"
              onClick={confirm.action}
            />
          </Inline>
        </Stack>
      </DialogBase>
    );
  }
);
